import { Component } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { CheckPageAccess } from 'src/app/components/authorize/check-page-access';
import { ROLE_ONBOARDING_VIDEO_MATERIAL_TYPE, RoleOnboardingCourse } from 'src/app/components/role-onboarding/role-onboarding';
import { ApplicationRoles, ApplicationRolesEnumDescriptionService } from 'src/app/components/users/user-roles';
import { FileService } from 'src/app/services/file.service';
import { RoleOnboardingService } from 'src/app/services/role-onboarding.service';
import { ToastService } from 'src/app/services/toast-service.service';

@Component({
    selector: 'app-edit-role-onboarding',
    templateUrl: './edit-role-onboarding.component.html',
    styleUrls: ['./edit-role-onboarding.component.scss'],
    standalone: false
})
export class EditRoleOnboardingComponent {
  course: RoleOnboardingCourse = {
    id: 0,
    role: ApplicationRoles.BB_Coach,
    roleName: '',
    title: '',
    videoUrl: '',
    videoFileId: null,
    videoFileName: null,
    description: '',
    isActive: true,
    isMandatory: true,
    displayOrder: 1,
    version: 1,
    status: 'Active'
  };
  isSubmitted = false;
  courseStatus = 'active';
  pageName = CheckPageAccess.EDITROLEONBOARDING;
  roles: { id: ApplicationRoles; name: string }[] = [];
  videoSource: 'link' | 'upload' = 'link';
  uploadedFileName = '';
  isUploading = false;
  uploadProgress = 0;
  isSaving = false;

  constructor(
    private roleOnboardingService: RoleOnboardingService,
    private fileService: FileService,
    private toast: ToastService,
    private router: Router,
    private route: ActivatedRoute,
    private roleDescriptions: ApplicationRolesEnumDescriptionService) {
    this.roles = Object.keys(ApplicationRoles)
      .filter(key => isNaN(Number(key)))
      .map(key => {
        const id = ApplicationRoles[key as keyof typeof ApplicationRoles];
        return { id, name: this.roleDescriptions.getDescription(id) };
      });
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.roleOnboardingService.getById(+params['id']).subscribe({
        next: (details) => {
          this.course = {
            ...details,
            videoUrl: details.videoUrl ?? '',
            videoFileId: details.videoFileId ?? null,
            videoFileName: details.videoFileName ?? null,
            displayOrder: Math.max(1, details.displayOrder || 1)
          };
          this.courseStatus = details.isActive ? 'active' : 'inactive';
          if (details.videoFileId) {
            this.videoSource = 'upload';
            this.uploadedFileName = details.videoFileName || `File #${details.videoFileId}`;
          } else {
            this.videoSource = 'link';
            this.uploadedFileName = '';
          }
        }
      });
    });
  }

  onVideoSourceChange(source: 'link' | 'upload'): void {
    this.videoSource = source;
  }

  onVideoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    this.isUploading = true;
    this.uploadProgress = 0;
    this.uploadedFileName = file.name;

    this.fileService.uploadMaterialFile(formData, ROLE_ONBOARDING_VIDEO_MATERIAL_TYPE).subscribe({
      next: (httpEvent) => {
        if (httpEvent.type === HttpEventType.UploadProgress && httpEvent.total) {
          this.uploadProgress = Math.min(99, Math.round((100 * httpEvent.loaded) / httpEvent.total));
        } else if (httpEvent.type === HttpEventType.Response) {
          this.course.videoFileId = httpEvent.body.id;
          this.course.videoFileName = httpEvent.body.fileName || file.name;
          this.course.videoUrl = '';
          this.uploadedFileName = this.course.videoFileName || file.name;
          this.uploadProgress = 100;
          this.isUploading = false;
        }
      },
      error: () => {
        this.isUploading = false;
        this.uploadProgress = 0;
        this.toast.showErrorMessage('Video upload failed. Please try again.');
        input.value = '';
      }
    });
  }

  stepOrder(delta: number): void {
    this.course.displayOrder = Math.max(1, (Number(this.course.displayOrder) || 1) + delta);
  }

  normalizeOrder(): void {
    this.course.displayOrder = Math.max(1, Math.floor(Number(this.course.displayOrder)) || 1);
  }

  save(): void {
    this.isSubmitted = true;
    this.course.isActive = this.courseStatus === 'active';

    if (!this.course.title.trim()) {
      return;
    }

    if (this.isUploading || this.isSaving) {
      return;
    }

    if (this.videoSource === 'link') {
      const url = (this.course.videoUrl ?? '').trim();
      if (!url) {
        return;
      }
      if (!this.isHttpsUrl(url)) {
        this.toast.showErrorMessage('Video URL must be a valid https link');
        return;
      }
      this.course.videoUrl = url;
      this.course.videoFileId = null;
      this.course.videoFileName = null;
    } else {
      if (!this.course.videoFileId) {
        this.toast.showErrorMessage('Please upload a video file');
        return;
      }
      this.course.videoUrl = null;
    }

    this.isSaving = true;
    this.roleOnboardingService.update(this.course).subscribe({
      next: (updated) => {
        this.isSaving = false;
        if (updated) {
          this.toast.showMessage('Role training updated successfully');
          this.router.navigateByUrl('/role-training-list');
        } else {
          this.toast.showErrorMessage('Unable to update role training');
        }
      },
      error: () => {
        this.isSaving = false;
        this.toast.showErrorMessage('Unable to update role training');
      }
    });
  }

  private isHttpsUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }
}
